import {
  Fragment,
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  SHOW_AI_NOTE,
  SHOW_JSD_LABEL,
  SHOW_MATCH_PERCENTAGE,
} from "@/config/displayPolicy";
import { usePersistentBoolean } from "@/hooks/usePersistentBoolean";
import { useAnalysisStore } from "@/stores/useAnalysisStore";
import type { FeatureData } from "@/types/spectrum";
import {
  ASSESSMENT_STATUS_LABEL,
  assessmentBadgeClass,
} from "@/utils/assessment";
import {
  type CompoundCategory,
  classifyCompound,
  getCategoryLabel,
} from "@/utils/compoundCategory";
import { getKoreanName } from "@/utils/compoundKoreanName";
import { formatRt } from "@/utils/formatRt";
import {
  countObservations,
  groupFeaturesByObservation,
  type Observation,
} from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";
import { displayVp, vpChipClass, vpTooltip } from "@/utils/vaporPressure";

// iter2.1 C-3: 위험 카테고리 색상 (forensic 위험도 톤)
const CATEGORY_CHIP_CLS: Record<CompoundCategory, string> = {
  halogenated: "bg-red-100 text-red-700",
  ketone_aldehyde: "bg-orange-100 text-orange-700",
  aromatic: "bg-yellow-100 text-yellow-700",
  petroleum_alkane: "bg-blue-100 text-blue-700",
  other: "bg-gray-100 text-gray-600",
};

const JSD_ABBR_TOOLTIP =
  "JSD: Jensen-Shannon Divergence (스펙트럼 유사도, 낮을수록 일치).";

// T1: 정렬·Top-N 단위는 후보 행이 아니라 **관찰(스캔)** 이다. 관찰의 의심 여부는
// 후보 중 하나라도 의심이면 참. 관찰 안은 관찰 내 순위순.
//
// 2026-08-31 (검출 표시 개선 1단계): 2차 정렬을 점수 → **관찰 신호(area)** 로
// 바꿨다. PI 지적("강도 큰 피크가 안 보인다")의 화면 쪽 원인 — 점수순 Top5 는
// 신호가 큰 스캔을 뒤로 밀었고, 임계값을 낮춰도 화면 기본 표가 한 글자도 안
// 바뀌는 것이 실측됐다. area 는 이제 관찰(스캔)의 실제 신호 합(배경 제외)이다.
// 신호 미상(0)은 자연히 뒤로 가고, 그 안에서는 점수순을 유지한다.
const TOP_N = 5;

function sortObservationsByPriority(
  observations: Observation[],
): Observation[] {
  const suspicious = (o: Observation) => o.features.some((f) => f.suspicious);
  const signal = (o: Observation) =>
    Math.max(...o.features.map((f) => f.area ?? 0));
  const score = (o: Observation) =>
    Math.max(...o.features.map((f) => f.matching_score ?? 0));
  return [...observations].sort((a, b) => {
    const sa = suspicious(a);
    const sb = suspicious(b);
    if (sa !== sb) return sa ? -1 : 1;
    const sig = signal(b) - signal(a);
    if (sig !== 0) return sig;
    return score(b) - score(a);
  });
}

/** 표시 RT 가 실측 스캔/저장 RT 가 아니라 추정·위치 대응이면 참. */
function isEstimatedRt(source: string | undefined): boolean {
  return source != null && source !== "scan" && source !== "stored";
}

const SAME_SCAN_TOOLTIP =
  "이 관찰(스캔)에 기록된 AI 후보들입니다. 후보끼리는 서로 독립된 검출이 아니라 " +
  "같은 스펙트럼에 대한 대안 후보(또는 공동용출)이며, 어느 후보를 눌러도 같은 참조물질 " +
  "순위표가 나옵니다. 독립 검출 건수로 세지 마십시오. " +
  "(2026-08-31 부터 같은 물질이 여러 스캔에서 나오면 각 스캔에 기록됩니다 — " +
  "이 목록은 이 스캔에 대해 모델이 낸 후보 중 내부 기준 이상 전부입니다.)";

/**
 * 이 표의 컬럼 수 (ID · 관찰 # · 물질 · 위험 카테고리 · [점수] · JSD · 물성 · [AI Note]).
 *
 * 표 전체를 가로지르는 행(관찰 헤더, 인라인 상세행)이 쓴다. 표시 플래그가 컬럼
 * 수를 바꾸므로 상수로 하드코딩하면 반드시 어긋난다 — 실제로
 * `FeatureDetailInlineRow` 가 `colSpan={7}` 로 굳어 있다가, '위험 카테고리'
 * 컬럼 추가(7→8)와 플래그 2개 비표시(8→6)를 거치며 드리프트했다(2026-08-28 발견).
 * 그래서 export 해서 공유한다 — 플래그를 되살리면 두 곳이 함께 따라온다.
 */
export const COLUMN_COUNT =
  6 + (SHOW_MATCH_PERCENTAGE ? 1 : 0) + (SHOW_AI_NOTE ? 1 : 0);

interface FeaturesTableProps {
  features: FeatureData[];
  selectedFeature: string | null;
  onFeatureSelect: (featureId: string) => void;
  isMsMissing?: boolean;
  /**
   * K2 hybrid responsive — tablet/mobile (<lg) 에서 선택된 행 바로 아래에
   * expand-row를 삽입하는 렌더러. desktop(>=lg) 에서는 무시.
   * SampleAnalysisPage가 viewport 기준으로 전달 여부를 결정.
   */
  inlineDetailRenderer?: (feature: FeatureData) => ReactNode;
}

/**
 * JSD 거리 → 글자색. 구간 경계(0.2 / 0.5)가 검증된 기준이 아니라 색으로도 해석을
 * 주지 않는다 — `SHOW_JSD_LABEL` 이 꺼져 있으면 중립색만 쓴다 (2026-08-27).
 */
function getJsdColor(jsd: number | undefined): string {
  if (jsd == null) return "text-gray-400";
  if (!SHOW_JSD_LABEL) return "text-gray-800";
  if (jsd < 0.2) return "text-green-600";
  if (jsd < 0.5) return "text-yellow-600";
  return "text-red-600";
}

function getSolLabel(sol: number): string {
  if (sol > 0) return "수용성";
  if (sol > -3) return "난용성";
  return "비수용";
}

function PropertyIndicator({
  predicted_vp,
  predicted_solubility,
}: {
  predicted_vp?: number;
  predicted_solubility?: number;
}) {
  const hasVp = predicted_vp != null;
  const hasSol = predicted_solubility != null;

  if (!hasVp && !hasSol) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      {/* 2026-08-28 (P2-4): 숫자·색·툴팁을 모두 같은 헬퍼에서 만든다. 그 전에는
          숫자만 원값이고 색·툴팁은 보정값(+2.0)이라 한 칩이 두 값을 말했다.
          근거: config/displayPolicy.ts 의 SHOW_VP_CORRECTION. */}
      {hasVp && predicted_vp != null && (
        <span
          className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${vpChipClass(predicted_vp)}`}
          title={vpTooltip(predicted_vp)}
        >
          VP {displayVp(predicted_vp).toFixed(1)}
        </span>
      )}
      {hasSol && (
        <span
          className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
            predicted_solubility! < -3
              ? "bg-red-100 text-red-700"
              : predicted_solubility! < 0
                ? "bg-yellow-100 text-yellow-700"
                : "bg-green-100 text-green-700"
          }`}
          title={`용해도: ${predicted_solubility?.toFixed(2)} log S — ${getSolLabel(predicted_solubility!)}`}
        >
          Sol {predicted_solubility?.toFixed(1)}
        </span>
      )}
    </div>
  );
}

const FeaturesTable = ({
  features,
  selectedFeature,
  onFeatureSelect,
  isMsMissing = false,
  inlineDetailRenderer,
}: FeaturesTableProps) => {
  // iter2 B-1: 기본 Top 5 노출 + 더보기 토글 — T1 부터 단위는 관찰(스캔)
  //
  // 2026-09-04: 이 토글도 **보기 방식**이므로 새로고침 뒤에도 유지한다
  // (운영자 요청 — 접기·펼치기 상태 전반). 판독 상태(선택한 후보·RT)는
  // 저장하지 않는다는 원칙은 그대로다.
  const [showAll, setShowAll] = usePersistentBoolean(
    "toxgas.dashboard.featuresShowAll",
    false,
  );
  const observations = useMemo(
    () => sortObservationsByPriority(groupFeaturesByObservation(features)),
    [features],
  );
  const { observationCount, candidateCount } = useMemo(
    () => countObservations(features),
    [features],
  );
  const hasMore = observations.length > TOP_N;
  // 2026-09-03: 접힌 상태(Top 5)에서 **선택한 관찰이 목록 밖이면** 표에
  // 하이라이트가 나타나지 않았다(운영자 지적: "선택하면 아래쪽 분석된 내용이
  // 하이라이트 안 된다"). 선택은 됐는데 그 행이 화면에 없던 것 —
  // 사용자가 고른 것은 항상 보여야 한다. 상위 N 에 없으면 뒤에 덧붙인다.
  const { displayed, appendedObservationId } = useMemo(() => {
    if (showAll) {
      return { displayed: observations, appendedObservationId: null };
    }
    const top = observations.slice(0, TOP_N);
    if (
      selectedFeature == null ||
      top.some((observation) =>
        observation.features.some((f) => f.feature_id === selectedFeature),
      )
    ) {
      return { displayed: top, appendedObservationId: null };
    }
    const selectedObservation = observations.find((observation) =>
      observation.features.some((f) => f.feature_id === selectedFeature),
    );
    return selectedObservation
      ? {
          displayed: [...top, selectedObservation],
          appendedObservationId: selectedObservation.id,
        }
      : { displayed: top, appendedObservationId: null };
  }, [observations, showAll, selectedFeature]);

  // 선택한 행이 표 안에서 안 보이면 **표 안에서만** 끌어온다.
  //
  // 2026-09-03 (운영자 지적 1): 상위 5 밖의 관찰을 차트에서 고르면 그 행이
  // 목록 **끝**에 붙는데, 표는 자체 스크롤 영역(max-h 480px) 안에 있어
  // "선택했는데 아무 일도 안 일어난 것처럼" 보였다.
  //
  // 2026-09-03 (운영자 지적 2): 그 해결로 넣은 `scrollIntoView` 가 **페이지
  // 전체를 움직였다.** block:"nearest" 는 "이미 보이면 안 움직인다"일 뿐,
  // 움직일 때는 표 내부 스크롤과 **문서 스크롤 양쪽에** 적용된다. 실측:
  //   · 최초 진입만으로 1,361px — 첫 화면에서 팩트 요약·AI 지표·탑 플롯이 사라짐
  //   · 탑 플롯의 점 클릭 시 +694px(최악 +997px) — **방금 누른 플롯이 화면 밖으로**
  //   · 그렇게 움직이고도 착지한 행은 화면 맨 아래 가장자리에 반쯤 걸렸다
  //
  // 그래서 문서는 건드리지 않고 표의 스크롤 영역만 움직인다(운영자 결정 3-a).
  // 8/31 에 해결한 "상위 5 밖 관찰이 표에 안 보인다"는 그대로 유지된다 —
  // 그 해결의 본체는 목록 끝에 행을 덧붙이는 로직(`displayed`)과 **표 내부**
  // 스크롤이지 페이지 스크롤이 아니었다.
  const selectedRowRef = useRef<HTMLTableRowElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  // 진입 자동 선택(JSD 최소 후보)으로는 표도 움직이지 않는다 — 사람이 고른
  // 것이 아닌데 표가 170px 내려간 채(1순위가 잘린 채) 시작하게 된다.
  // `autoSelectDone` 은 그 자동 선택이 걸리는 순간 false→true 로 바뀐다.
  // 마운트 시점에 아직 false 였다면, 우리가 처음 보게 될 선택 변경이 그것이다.
  const autoSelectPendingRef = useRef(
    !useAnalysisStore.getState().autoSelectDone,
  );
  useEffect(() => {
    if (!selectedFeature) return;

    if (autoSelectPendingRef.current) {
      autoSelectPendingRef.current = false;
      // 자동 선택이 실제로 걸린 것이면 이번 변경은 건너뛴다. 사람이 자동
      // 선택보다 먼저 골랐다면(그러면 자동 선택은 걸리지 않는다) 그대로 진행한다.
      if (useAnalysisStore.getState().autoSelectDone) return;
    }

    const area = scrollAreaRef.current;
    const row = selectedRowRef.current;
    if (!area || !row) return;

    const areaRect = area.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    // 이미 보이면 움직이지 않는다(block:"nearest" 가 하던 성질을 유지).
    if (rowRect.top >= areaRect.top && rowRect.bottom <= areaRect.bottom)
      return;

    // 가운데로 — 열 헤더가 sticky 라 위에 붙여 놓으면 헤더에 가린다.
    const delta =
      rowRect.top - areaRect.top - (areaRect.height - rowRect.height) / 2;
    area.scrollTo({ top: area.scrollTop + delta, behavior: "smooth" });
  }, [selectedFeature]);

  return (
    <div className={panelCls("standard", "h-full")}>
      {/* 2026-08-28: 이름을 「AI 동정 후보」로 바꿨다. 같은 화면에 참조 순위표
          (SpectrumHitList)가 있는데 옛 이름 "후보 물질 목록" / "후보 목록"이 한
          단어 차이라 어느 표를 가리키는지 알 수 없었다. 두 표는 보는 것이 다르다 —
          이 표는 인하대 JSD 동정 결과(저장값), 순위표는 참조 스펙트럼 코사인
          (조회 시점 재계산)이다. 근거: docs/2026/260827_대시보드_두섹션_필요성_분석.md P1. */}
      {/* 2026-08-28 정정: 제목에 붙였던 부제 "(인하대 JSD)" 를 뺐다. 동정 엔진은
          **행 단위 값**(`identification_engine` 은 'inha_jsd'·'mock'·NULL)이고,
          features API 가 0건이면 화면이 프론트 하드코딩 mockup 으로 대체된다.
          그런 행 위에 기관 이름을 단정하면 표만 잘라낸 화면·보고서에서 허위
          귀속이 된다. 엔진 표기는 행 단위로 낼 수 있을 때 다시 검토한다. */}
      <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center flex-wrap">
        <span className="w-2 h-2 bg-indigo-500 rounded-full mr-2" />
        AI 동정 후보
        {/* T1: 관찰(스캔) 수와 후보 수를 구분 — 후보 행을 검출 건수로 세지 않는다 */}
        <span
          className="ml-2 text-xs font-normal text-gray-500"
          title="관찰 = AI 후보가 기록된 MS 스캔 1개. 같은 스캔의 후보 여러 개는 한 관찰로 묶습니다. 같은 물질이 여러 스캔에서 나오면 각 스캔에 기록되므로, 후보 수는 물질 종 수가 아닙니다."
        >
          관찰 {observationCount}건 · 후보 {candidateCount}건
        </span>
      </h3>
      {/* JSD 범례 — 해석 라벨은 검증된 기준이 정해지기 전까지 내지 않는다 */}
      <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-gray-500">
        {SHOW_JSD_LABEL ? (
          <>
            <span className="font-medium text-gray-600">JSD 거리:</span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-green-500" />
              &lt;0.2 신뢰 높음
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-yellow-500" />
              0.2~0.5 확인 필요
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-500" />
              &gt;0.5 매칭 약함
            </span>
          </>
        ) : (
          <span>
            <span className="font-medium text-gray-600">JSD 거리</span> —
            스펙트럼 유사도(Jensen-Shannon). 낮을수록 참조와 가깝습니다. 판정
            기준은 과제 내에서 정해지는 대로 반영합니다.
          </span>
        )}
      </div>
      {/* P2-2: container height 고정 — showAll 토글 시 layout reflow 방지.
          min-h로 초기 Top-5 높이 확보, max-h + overflow-y-auto로 스크롤 격리. */}
      {/* S24: @container — 저정보 컬럼(ID/Peak No/물성)을 테이블 실제 칸 폭
          기준으로 접어 가로 스크롤 방지(뷰포트 아님, 컨테이너 폭 기준). */}
      <div
        ref={scrollAreaRef}
        className="@container overflow-x-auto min-h-[200px] max-h-[480px] overflow-y-auto"
      >
        {displayed.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 text-center">
            <p className="text-sm text-gray-600">
              {isMsMissing
                ? "화합물 동정 불가 — MS 미수집 (N/A, 비검출 아님)"
                : "AI 후보 없음"}
            </p>
          </div>
        ) : (
          <table className="min-w-full text-sm">
            {/* 2026-09-03: 헤더 고정. 표는 자체 스크롤 영역(max-h 480px) 안에
                있고 전체 보기에서는 관찰 수십 건이 이어진다 — 스크롤하면 열
                이름이 사라져 숫자가 무슨 값인지 알 수 없었다. 선택한 관찰로
                자동 스크롤하는 동작이 생기면서 더 깊은 위치로 내려가므로
                더욱 필요하다. 배경은 불투명해야 행이 비쳐 보이지 않는다. */}
            <thead>
              <tr className="border-b border-gray-200">
                <th className="hidden @min-[600px]:table-cell sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  ID
                </th>
                {/* T1: 관찰(스캔) 번호 — 같은 스캔 후보끼리 같다 */}
                <th
                  className="hidden @min-[600px]:table-cell sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide"
                  title="관찰(스캔) 번호 — RT 오름차순. 같은 스캔의 후보는 같은 번호."
                >
                  관찰 #
                </th>
                <th className="sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  물질
                </th>
                {/* iter2.1 C-3: 위험 카테고리 컬럼 + 시연 caveat */}
                <th className="sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  위험 카테고리{" "}
                </th>
                {SHOW_MATCH_PERCENTAGE && (
                  <th className="sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    점수
                  </th>
                )}
                <th className="sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  JSD 거리{" "}
                  <span
                    role="img"
                    aria-label={JSD_ABBR_TOOLTIP}
                    title={JSD_ABBR_TOOLTIP}
                    className="opacity-60 cursor-help"
                  >
                    ⓘ
                  </span>
                </th>
                <th className="hidden @min-[600px]:table-cell sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  물성
                </th>
                {SHOW_AI_NOTE && (
                  <th className="sticky top-0 z-10 bg-white text-left py-2 px-2 text-xs font-medium text-gray-500 uppercase tracking-wide">
                    AI Note
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayed.map((observation) => (
                <Fragment key={`obs-${observation.id}`}>
                  {/* T1: 관찰(스캔) 헤더 — 같은 스캔 후보는 이 아래에 연속 */}
                  <tr
                    data-testid={`observation-${observation.id}`}
                    className="bg-slate-50"
                  >
                    <td
                      colSpan={COLUMN_COUNT}
                      className="py-1.5 px-2 text-[11px] text-slate-600"
                    >
                      <span className="font-semibold text-slate-700">
                        관찰 {observation.peakNo}
                      </span>
                      <span className="mx-1.5 text-slate-300">|</span>
                      <span className="font-mono">
                        RT {formatRt(observation.rt)} min
                        {isEstimatedRt(observation.rtSource) ? " (추정)" : ""}
                      </span>
                      <span className="mx-1.5 text-slate-300">|</span>
                      <span>후보 {observation.size}개</span>
                      {/* 상위 5 밖인데 선택돼서 끌어온 관찰 — 순위가 6번째라는
                          뜻으로 오해하지 않게 이유를 적는다. */}
                      {observation.id === appendedObservationId && (
                        <span
                          className="ml-2 inline-block rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700"
                          title="신호 상위 5건 밖이지만 차트에서 선택해 표에 표시했습니다"
                        >
                          선택한 관찰 (상위 5 밖)
                        </span>
                      )}
                      {observation.size > 1 && (
                        <span
                          className="ml-2 inline-block rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 cursor-help"
                          title={SAME_SCAN_TOOLTIP}
                        >
                          같은 스캔 후보 — 독립 검출 아님 · 순위표 공유
                        </span>
                      )}
                    </td>
                  </tr>
                  {observation.features.map((feature) => {
                    const isSelected = selectedFeature === feature.feature_id;
                    return (
                      <Fragment key={feature.feature_id}>
                        <tr
                          ref={isSelected ? selectedRowRef : undefined}
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-blue-50 border-l-4 border-blue-500"
                              : ""
                          }`}
                          onClick={() => onFeatureSelect(feature.feature_id)}
                        >
                          <td className="hidden @min-[600px]:table-cell py-2 px-2 font-mono text-xs">
                            {feature.feature_id}
                          </td>
                          <td className="hidden @min-[600px]:table-cell py-2 px-2 text-xs">
                            {feature.peak_no}
                          </td>
                          {/* iter2.2 #3: 화합물명 한국어 통용명 + 영문 보조 */}
                          <td className="py-2 px-2 text-xs font-medium">
                            {(() => {
                              const { ko, en, hasMapping } = getKoreanName(
                                feature.predicted_substance,
                              );
                              return (
                                <div className="flex flex-col leading-tight">
                                  <span>
                                    {ko}
                                    {/* T2-3: 감정인 판정 배지 — 사람이 기록한 판단만 */}
                                    {feature.assessment_status && (
                                      <span
                                        className={`ml-1.5 inline-block rounded px-1 py-0.5 text-[10px] font-medium align-middle ${assessmentBadgeClass(feature.assessment_status)}`}
                                        title="감정인이 기록한 판정 상태 (상세 패널에서 근거 확인)"
                                      >
                                        판정(감정인):{" "}
                                        {ASSESSMENT_STATUS_LABEL[
                                          feature.assessment_status
                                        ] ?? feature.assessment_status}
                                      </span>
                                    )}
                                  </span>
                                  {hasMapping && (
                                    <span className="text-[10px] text-gray-400 font-normal">
                                      {en}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          {/* iter2.1 C-3: 위험 카테고리 셀
                          iter2.2: chip label에 asterisk(*) 추가 — 시연용 분류 caveat 시각 신호 */}
                          <td className="py-2 px-2 text-xs">
                            {(() => {
                              const cat = classifyCompound(
                                feature.smiles,
                                feature.predicted_substance,
                              );
                              return (
                                <span
                                  className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${CATEGORY_CHIP_CLS[cat]}`}
                                >
                                  {getCategoryLabel(cat)}*
                                </span>
                              );
                            })()}
                          </td>
                          {SHOW_MATCH_PERCENTAGE && (
                            <td className="py-2 px-2 text-xs">
                              {feature.matching_score}%
                            </td>
                          )}
                          <td
                            className={`py-2 px-2 text-xs font-medium ${getJsdColor(feature.jsd_distance)}`}
                            title={
                              feature.jsd_distance == null
                                ? ""
                                : SHOW_JSD_LABEL
                                  ? feature.jsd_distance < 0.2
                                    ? "신뢰 높음 — 참조 스펙트럼과 거의 일치"
                                    : feature.jsd_distance < 0.5
                                      ? "추가 확인 필요 — 유사하지만 차이 있음"
                                      : "매칭 약함 — 다른 물질이거나 혼합물 가능성"
                                  : "JSD 거리 — 낮을수록 참조 스펙트럼과 가깝습니다"
                            }
                          >
                            {feature.jsd_distance != null
                              ? feature.jsd_distance.toFixed(3)
                              : "—"}
                          </td>
                          <td className="hidden @min-[600px]:table-cell py-2 px-2 text-xs">
                            <PropertyIndicator
                              predicted_vp={feature.predicted_vp}
                              predicted_solubility={
                                feature.predicted_solubility
                              }
                            />
                          </td>
                          {SHOW_AI_NOTE && (
                            <td
                              className="py-2 px-2 text-xs text-gray-600 max-w-[150px] @min-[600px]:max-w-xs truncate"
                              title={feature.ai_note}
                            >
                              {feature.ai_note}
                            </td>
                          )}
                        </tr>
                        {/* K2 hybrid: tablet/mobile expand-row (inlineDetailRenderer 존재 + 선택됨) */}
                        {isSelected &&
                          inlineDetailRenderer &&
                          inlineDetailRenderer(feature)}
                      </Fragment>
                    );
                  })}
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* iter2 B-1: 5건 초과 시 더보기 토글 */}
      {hasMore && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((prev) => !prev)}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-2 py-1"
          >
            {showAll
              ? `Top ${TOP_N} 관찰만 보기`
              : `전체 관찰 ${observations.length}건 보기 (후보 ${candidateCount}건)`}
          </button>
        </div>
      )}
      {/* iter2.2: 위험 카테고리 caveat footnote — 항상 노출 (chip * 표시 보완) */}
      {/* 2026-08-28: 각주가 자기 출처를 틀리게 말하고 있었다 — "화합물명을
          바탕으로"라고 썼지만 실제 1순위 입력은 SMILES 이고(utils/compoundCategory
          의 classifyCompound), 이름은 SMILES 가 없을 때만 쓰는 fallback 이다.
          그 탓에 독자가 실패 모드를 반대로 예상한다: 실제 오분류는 이름이 아니라
          SMILES **표기법** 차이에서 나온다(같은 엔진 응답 안에서 Kekulé 표기
          `CC1=CC=CC=C1`(톨루엔)은 '기타'로 빠지고 소문자 `CCc1ccccc1`
          (에틸벤젠)만 '방향족'으로 잡힌다). 근거를 사실대로 적는다. */}
      <p
        data-testid="risk-category-footnote"
        className="mt-3 text-[10px] text-gray-500 leading-snug"
      >
        * 위험 카테고리는 화합물의 구조식(SMILES)을, 없으면 화합물명을 바탕으로
        한 표시용 분류입니다. 화면에서만 쓰는 분류이며 분석 결과에 저장되지
        않습니다.{" "}
        <strong className="font-semibold">최종 감정 판정이 아니며</strong>, 최종
        판단에는 전문가 검토가 필요합니다.
        {/* 2026-09-03: 이 면책은 종전 「분류」 열 헤더의 ⓘ 툴팁에만 있었다.
            마우스를 올려야만 보이는 문구는 시연·터치 환경에서 사실상 없는
            것과 같아, 표 아래 상시 노출로 옮겼다(운영자 지시). */}
      </p>
    </div>
  );
};

export default memo(FeaturesTable);
